import React, { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Quote, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { useContent } from "@/lib/useContent";

export const TestimonialsCarousel = () => {
  const testimonials = useContent("testimonials");
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, align: "start" });
  const [selected, setSelected] = useState(0);
  useEffect(() => {
    if (!embla) return;
    const onSelect = () => setSelected(embla.selectedScrollSnap());
    embla.on("select", onSelect);
    const auto = setInterval(() => embla.scrollNext(), 5500);
    return () => { clearInterval(auto); embla.off("select", onSelect); };
  }, [embla]);
  const prev = useCallback(() => embla && embla.scrollPrev(), [embla]);
  const next = useCallback(() => embla && embla.scrollNext(), [embla]);

  return (
    <section className="py-24 lg:py-32 bg-[#F9FAFB]" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading eyebrow="Client Testimonials" title="Loved by travellers worldwide" subtitle="Authentic reviews from clients across three continents." />
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="flex-none w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]">
                <div className="bg-white border border-[#E5E7EB] rounded-2xl p-8 h-full shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                  <div className="flex items-center justify-between">
                    <Quote size={28} className="text-[#D4A62A]" strokeWidth={1.2} />
                    <span className="text-[11px] uppercase tracking-wider font-bold text-[#0A2E4D] bg-[#F3F4F6] rounded-full px-3 py-1">{t.service || t.role}</span>
                  </div>
                  <p className="text-[#1F2937] mt-4 leading-relaxed text-sm min-h-[110px]">{t.text}</p>
                  <div className="flex items-center gap-1 mt-5">{Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={i} size={14} className="text-[#D4A62A] fill-[#D4A62A]" />)}</div>
                  <div className="mt-5 pt-5 border-t border-[#E5E7EB] flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-[#0A2E4D] text-[#E3BA45] flex items-center justify-center font-serif-display text-lg">{t.name.charAt(0)}</div>
                    <div>
                      <div className="font-semibold text-[#0A2E4D] text-sm">{t.name}</div>
                      <div className="text-xs text-[#4B5563]">{t.country}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-center gap-4 mt-10">
          <button onClick={prev} aria-label="Previous" className="w-11 h-11 rounded-full border border-[#E5E7EB] flex items-center justify-center text-[#0A2E4D] hover:bg-[#0A2E4D] hover:text-white transition-colors" data-testid="testimonials-prev-btn"><ChevronLeft size={18} /></button>
          <div className="flex gap-2">{testimonials.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === selected ? "w-6 bg-[#D4A62A]" : "w-2 bg-[#E5E7EB]"}`} />)}</div>
          <button onClick={next} aria-label="Next" className="w-11 h-11 rounded-full border border-[#E5E7EB] flex items-center justify-center text-[#0A2E4D] hover:bg-[#0A2E4D] hover:text-white transition-colors" data-testid="testimonials-next-btn"><ChevronRight size={18} /></button>
        </div>
      </div>
    </section>
  );
};
